import { useCallback, useEffect, useRef, useState } from 'react';
import SelectionActionBar, {
  type SelectionAction,
} from '../components/SelectionActionBar';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  ConfirmationDialog,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Download, Layers, Loader2, Trash2, X, UserPlus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../store';
import { PermissionSlug } from '../constants/featureFlags';
import PageEditorDialog from '../components/faxInbox/PageEditorDialog';
import {
  listFaxTypeOptions,
  removeFaxItem,
  removeFaxItems,
  searchPatients,
  type FaxTypeOption,
  type PatientOption,
} from '../services/faxClassification';
import {
  createIntakeFromFaxDocuments,
  getFaxDocumentUrl,
} from '../services/faxDocuments';
import {
  getReceivedFaxDocumentUrl,
  getReceivedFax,
} from '../services/faxInbox';
import {
  listFaxItems,
  mergeFaxItems,
  type FaxItem,
} from '../services/faxItems';
import { formatDateTimeInPT } from '../utils/dateUtils';

const PAGE_SIZE = 25;

/** No type filter. Radix cannot hold an empty string as a Select value. */
const ANY_TYPE = '__any__';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'processing', label: 'Processing' },
  { value: 'ready', label: 'Ready' },
  { value: 'failed', label: 'Failed' },
];

/**
 * What a status means to somebody reading the inbox, rather than what the
 * column stores. 'received' exists in the check constraint but a fax reaching
 * this list has always moved past it.
 */
type BadgeTone =
  'default' | 'secondary' | 'destructive' | 'neutral' | 'outline';

const STATUS_LABELS: Record<string, { label: string; tone: BadgeTone }> = {
  processing: { label: 'Processing', tone: 'secondary' },
  ready: { label: 'Ready', tone: 'default' },
  failed: { label: 'Could not be read', tone: 'destructive' },
};

function StatusBadge({ status }: { status: string }) {
  // An unrecognised status is shown as itself rather than hidden or guessed at.
  // The set can grow on the server before it grows here, and a fax whose state
  // this build does not know about is still a fax somebody needs to see.
  const known = STATUS_LABELS[status];
  return (
    <Badge variant={known?.tone ?? 'outline'}>{known?.label ?? status}</Badge>
  );
}

/**
 * A fax number as it arrived, or a placeholder.
 *
 * Never "Unknown": a blank here means RingCentral did not tell us, which is
 * different from a sender who withheld the number, and the inbox should not
 * imply it knows which.
 */
function faxNumber(value: string | null): string {
  return value && value.trim() ? value : '—';
}

/**
 * Who a fax came from: the physician when the number names one, with the
 * number underneath, and otherwise the number alone.
 */
function SenderCell({ item }: { item: FaxItem }) {
  if (!item.sender_physician_name)
    return <>{faxNumber(item.fax_number_from)}</>;
  return (
    <div>
      <div className='font-medium'>{item.sender_physician_name}</div>
      <div className='text-xs text-muted-foreground'>
        {item.fax_number_from}
      </div>
    </div>
  );
}

/**
 * Whether the referral a document started is still being extracted.
 *
 * Reads the intake's own status rather than guessing from what the form does
 * or does not hold yet, so this says the same thing the Intake Manager says. A
 * referral whose extraction failed is not processing: somebody has to open it
 * to see why.
 */
function isExtracting(item: FaxItem): boolean {
  return item.intake_processing_status === 'processing';
}

/**
 * What to call a row that nobody has named.
 *
 * A stand-in rather than a stored default, so the rename field opens empty and
 * somebody is naming the thing rather than editing a sentence we wrote. An
 * assembled document always has a name, because naming it is part of making it.
 */
function displayName(item: FaxItem): string {
  if (item.name && item.name.trim()) return item.name;
  if (item.fax_number_from && item.fax_number_from.trim()) {
    return `Fax from ${item.fax_number_from}`;
  }
  return 'Fax';
}

/** Unique across both tables, so one selection can hold either kind. */
function itemKey(item: { kind: string; id: string }): string {
  return `${item.kind}:${item.id}`;
}

export default function FaxInbox() {
  const { showError, showSuccess, hasPermission, hasProduct } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
      hasPermission: state.hasPermission,
      hasProduct: state.hasProduct,
    }))
  );
  const canManage =
    hasProduct('fax') && hasPermission(PermissionSlug.FAX_INBOX_MANAGE);
  const canStartReferral = hasPermission(PermissionSlug.INTAKE_ACCESS);
  const navigate = useNavigate();

  const [items, setItems] = useState<FaxItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState('all');
  const [senderInput, setSenderInput] = useState('');
  const [senderFilter, setSenderFilter] = useState('');
  // Filed items are hidden by default: the inbox is a working list, and a
  // transmission every page of which already feeds a document is finished work.
  const [includeFiled, setIncludeFiled] = useState(false);
  const [patientFilter, setPatientFilter] = useState<PatientOption | null>(
    null
  );
  const [patientQuery, setPatientQuery] = useState('');
  const [patientMatches, setPatientMatches] = useState<PatientOption[]>([]);
  const [patientOpen, setPatientOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>(ANY_TYPE);
  const [typeOptions, setTypeOptions] = useState<FaxTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editing, setEditing] = useState<FaxItem | null>(null);
  const [removing, setRemoving] = useState<FaxItem | null>(null);
  const [removingMany, setRemovingMany] = useState<FaxItem[] | null>(null);
  // The records themselves, not their ids. A selection gathered across pages
  // has to survive the page it was made on: filtering the page in hand for
  // ticked ids would silently drop everything chosen on another one.
  const [selectedByKey, setSelectedByKey] = useState<Map<string, FaxItem>>(
    new Map()
  );

  // Which load is the current one. Responses can come back out of order,
  // because each list runs a count over the union, and an older one landing
  // last would show rows that do not match the filter or the page the controls
  // are displaying.
  const loadGeneration = useRef(0);

  // Keep an open dialog on the freshest copy of its row. Saving reloads the
  // list, and a dialog still holding the row it opened with goes on offering a
  // question that has already been answered, and links to where the order used
  // to be. Settles after one pass: the next render finds the same object.
  useEffect(() => {
    if (!editing) return;
    const fresh = items.find(
      item => item.id === editing.id && item.kind === editing.kind
    );
    if (fresh && fresh !== editing) setEditing(fresh);
  }, [items, editing]);

  const load = useCallback(async () => {
    // Not fetched without the permission. The refusal below is a render-time
    // early return, and effects run regardless of it, so without this guard
    // somebody who may not read the inbox still fires a request that 403s and
    // gets a failure toast on top of the screen telling them why they cannot
    // be here.
    if (!canManage) return;
    const generation = ++loadGeneration.current;
    setLoading(true);
    try {
      const result = await listFaxItems(
        PAGE_SIZE,
        page * PAGE_SIZE,
        includeFiled,
        status === 'all' ? undefined : status,
        senderFilter || undefined,
        patientFilter?.id,
        typeFilter === ANY_TYPE ? undefined : typeFilter
      );
      if (generation !== loadGeneration.current) return;
      setItems(result.items);
      setTotal(result.total);
      setLoadFailed(false);
    } catch (error) {
      if (generation !== loadGeneration.current) return;
      setItems([]);
      setTotal(0);
      setLoadFailed(true);
      console.error('Failed to load the fax inbox', error);
      showError('Could not load the fax inbox.');
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [
    canManage,
    includeFiled,
    page,
    patientFilter,
    senderFilter,
    showError,
    status,
    typeFilter,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  useEffect(() => {
    // A list can shrink underneath the controls. Left on a page past the end,
    // the table renders empty, which reads as the inbox having broken rather
    // than as it having moved on.
    if (page > lastPage) setPage(lastPage);
  }, [lastPage, page]);

  const selectedItems = Array.from(selectedByKey.values());
  const allVisibleSelected =
    items.length > 0 && items.every(item => selectedByKey.has(itemKey(item)));

  const toggleItem = (item: FaxItem) => {
    setSelectedByKey(current => {
      const next = new Map(current);
      const key = itemKey(item);
      if (next.has(key)) next.delete(key);
      else next.set(key, item);
      return next;
    });
  };

  const deselectItem = (item: FaxItem) => {
    setSelectedByKey(current => {
      const next = new Map(current);
      next.delete(itemKey(item));
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedByKey(current => {
      const next = new Map(current);
      // The rows on screen only, matching the Orders Manager: a header tick
      // that silently took the other pages too would be a very large action
      // behind a very small control. Anything chosen on another page stays
      // chosen.
      if (allVisibleSelected) items.forEach(item => next.delete(itemKey(item)));
      else items.forEach(item => next.set(itemKey(item), item));
      return next;
    });
  };

  /**
   * Staple the chosen rows into one, then open it.
   *
   * Named from the first row rather than asking first, and the editor opens on
   * the result: naming a document is easier once you can see what is in it, and
   * a prompt before the merge is a dialog in front of the thing it describes.
   */
  const mergeSelection = async (chosen: FaxItem[]) => {
    // No guard of its own: SelectionActionBar already refuses a second run
    // while this one is in flight, which is what stops one selection being
    // merged twice into two documents.
    try {
      const created = await mergeFaxItems(
        displayName(chosen[0]),
        chosen.map(item => ({ kind: item.kind, id: item.id }))
      );
      showSuccess(
        chosen.length > 1
          ? `Merged ${chosen.length} faxes into one.`
          : 'Made a document from that fax.'
      );
      // Cleared only once it exists: a failed merge leaves the selection so
      // the work of gathering it is not lost. What was in it has either been
      // filed or removed, so none of it is still a row to act on.
      setSelectedByKey(new Map());
      await load();
      // Built from what the server actually created, not from what was asked
      // for: the editor saves against this version, and a guessed one would
      // either pass by luck or be refused with a message about a version
      // nobody chose.
      setEditing({
        kind: 'document',
        id: created.id,
        name: created.name,
        page_count: created.page_count,
        received_at: new Date().toISOString(),
        status: 'ready',
        fax_number_from: null,
        version: created.version,
        filed: false,
      });
    } catch (error) {
      console.error('Failed to merge those faxes', error);
      showError('Could not merge those faxes.');
    }
  };

  /**
   * Start one referral from everything chosen.
   *
   * A referral is built from documents, so anything that is still only a
   * transmission is turned into one first. That leaves the pages the sender
   * actually transmitted exactly as they arrived, on record, and is the same
   * promotion the editor does when somebody saves an arrival.
   *
   * Sequential rather than parallel, because the order is the order the
   * referral reads in and extraction takes them as given.
   */
  const startReferral = async (chosen: FaxItem[]) => {
    try {
      const documentIds: string[] = [];
      for (const item of chosen) {
        if (item.kind === 'document') {
          documentIds.push(item.id);
          continue;
        }
        const promoted = await mergeFaxItems(displayName(item), [
          { kind: 'arrival', id: item.id },
        ]);
        documentIds.push(promoted.id);
      }
      await createIntakeFromFaxDocuments(documentIds);
      // Cleared only once the referral exists: a failed call leaves the
      // selection so the work of gathering it is not lost.
      setSelectedByKey(new Map());
      showSuccess(
        chosen.length > 1
          ? `Started a referral from ${chosen.length} faxes.`
          : 'Started a referral.'
      );
      // The Intake Manager rather than the referral itself. Extraction has
      // only just been queued, so the form would open empty and fill in under
      // the reader; the manager is the screen that shows that work happening.
      navigate('/intake-manager');
    } catch (error) {
      console.error('Failed to start a referral', error);
      showError('Could not start a referral from those faxes.');
    }
  };

  /** Take everything chosen out of the inbox, once that is confirmed. */
  const removeSelection = async (chosen: FaxItem[]) => {
    try {
      await removeFaxItems(
        chosen.map(item => ({ kind: item.kind, id: item.id }))
      );
      showSuccess(
        chosen.length > 1
          ? `Removed ${chosen.length} faxes from the inbox.`
          : 'Removed from the inbox.'
      );
      setSelectedByKey(new Map());
      setRemovingMany(null);
      await load();
    } catch (error) {
      console.error('Failed to remove those faxes', error);
      showError('Could not remove those faxes.');
    }
  };

  const selectionActions: SelectionAction<FaxItem>[] = [
    {
      key: 'merge',
      label: chosen =>
        chosen.length > 1
          ? `Merge these ${chosen.length} into one fax`
          : 'Make a document from this fax',
      icon: Layers,
      // Named rather than silently dropped: a fax still processing has no
      // pages to contribute, and merging without it would quietly produce a
      // document missing the pages somebody selected it for.
      disabledReason: chosen =>
        chosen.every(item => item.status === 'ready')
          ? undefined
          : 'Every chosen fax has to be ready first.',
      run: mergeSelection,
    },
    ...(canStartReferral
      ? [
          {
            key: 'create-referral',
            label: (chosen: FaxItem[]) =>
              chosen.length > 1
                ? `Create a referral from these ${chosen.length}`
                : 'Create a referral from this fax',
            icon: UserPlus,
            disabledReason: (chosen: FaxItem[]) =>
              chosen.every(item => item.status === 'ready')
                ? undefined
                : 'Every chosen fax has to be ready first.',
            run: startReferral,
          } satisfies SelectionAction<FaxItem>,
        ]
      : []),
    // Last, and on its own: everything above makes something out of the
    // selection, this throws it away. A destructive item sitting between two
    // constructive ones is the one somebody hits by accident.
    {
      key: 'remove',
      label: chosen =>
        chosen.length > 1
          ? `Remove these ${chosen.length} from the inbox`
          : 'Remove from the inbox',
      icon: Trash2,
      destructive: true,
      // Asked for rather than run: the rows disappear, and somebody who
      // cleared the wrong selection has no way back from this screen.
      run: async chosen => setRemovingMany(chosen),
    },
  ];

  /**
   * Take a fax out of the working list.
   *
   * Confirmed first, because the row disappears and the person who removed
   * the wrong one has no way back from this screen. What actually happens is
   * a date, so nothing is destroyed and support can restore it.
   */
  useEffect(() => {
    if (!canManage) return;
    void (async () => {
      try {
        // Retired ones included: this is for finding faxes, not filing them,
        // and retiring a type must not hide what is already under it.
        setTypeOptions(await listFaxTypeOptions(true));
      } catch (error) {
        console.error('Failed to load the fax types', error);
      }
    })();
  }, [canManage]);

  useEffect(() => {
    const typed = patientQuery.trim();
    if (!typed) {
      setPatientMatches([]);
      return;
    }
    // Debounced, the same as the picker in the editor: a query per keystroke.
    const timer = setTimeout(() => {
      void (async () => {
        try {
          setPatientMatches(await searchPatients(typed));
        } catch (error) {
          console.error('Failed to search patients', error);
          showError('Could not search patients.');
        }
      })();
    }, 250);
    return () => clearTimeout(timer);
  }, [patientQuery, showError]);

  const remove = async (item: FaxItem) => {
    try {
      await removeFaxItem(item.kind, item.id);
      showSuccess('Removed from the inbox.');
      // Dropped from the selection too. The selection deliberately survives
      // paging and reloading, so a removed row would otherwise keep counting
      // towards it and an action would run against a fax that is gone.
      deselectItem(item);
      setRemoving(null);
      await load();
    } catch (error) {
      console.error('Failed to remove the fax', error);
      showError('Could not remove that fax.');
    }
  };

  const openItem = useCallback(
    async (item: FaxItem) => {
      // Opened before the await, not after it. A window.open that follows an
      // await has lost the user gesture and is what pop-up blockers stop, and
      // the handle cannot be checked once noopener is in the feature string:
      // that form returns null whether it opened or not, so the old check
      // reported every successful download as blocked.
      const tab = window.open('', '_blank');
      if (!tab) {
        showError(
          'Your browser blocked the download. Allow pop-ups for this site and try again.'
        );
        return;
      }
      try {
        if (item.kind === 'document') {
          tab.location.href = await getFaxDocumentUrl(item.id);
          return;
        }
        // A transmission can carry several attachments. The first readable one
        // is what opening it means, and the editor is where the rest are.
        const fax = await getReceivedFax(item.id);
        const ready = fax.documents.find(
          document => document.status === 'ready'
        );
        if (!ready) {
          tab.close();
          showError('That fax has no pages that can be opened yet.');
          return;
        }
        tab.location.href = await getReceivedFaxDocumentUrl(fax.id, ready.id);
      } catch {
        tab.close();
        showError('Could not open that fax.');
      }
    },
    [showError]
  );

  // One gate, at the top. The sidebar hides the entry and every endpoint
  // refuses without this, so somebody arriving by URL would otherwise get a
  // screen of failed requests rather than an answer.
  if (!canManage) {
    return (
      <div>
        <p className='text-muted-foreground'>
          You do not have permission to view the fax inbox. Please contact your
          administrator if you need access.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardContent className='space-y-4'>
          <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center'>
            <Select
              value={status}
              onValueChange={value => {
                setStatus(value);
                setPage(0);
              }}
            >
              <SelectTrigger
                className='w-full sm:w-48'
                aria-label='Filter by status'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              aria-label='Filter by sender'
              placeholder='From number'
              className='w-full sm:w-56'
              value={senderInput}
              onChange={event => setSenderInput(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  // Trimmed, or a stray space makes this a different filter to
                  // the load effect and re-fires an identical request.
                  setSenderFilter(senderInput.trim());
                  setPage(0);
                }
              }}
              onBlur={() => {
                setSenderFilter(senderInput.trim());
                setPage(0);
              }}
            />
            {/* Narrows the whole list rather than only the transmissions,
                because a patient is something both kinds carry. */}
            <div className='relative w-full sm:w-56'>
              {patientFilter ? (
                <div className='flex h-10 items-center justify-between rounded-md border px-3'>
                  <span className='truncate text-sm'>
                    {patientFilter.last_name}, {patientFilter.first_name}
                  </span>
                  <Button
                    variant='ghost'
                    size='sm'
                    aria-label='Clear patient filter'
                    onClick={() => {
                      setPatientFilter(null);
                      setPage(0);
                    }}
                  >
                    <X className='h-4 w-4' />
                  </Button>
                </div>
              ) : (
                <>
                  <Input
                    aria-label='Filter by patient'
                    placeholder='Patient'
                    value={patientQuery}
                    onChange={event => setPatientQuery(event.target.value)}
                    onFocus={() => setPatientOpen(true)}
                    onBlur={() => setPatientOpen(false)}
                  />
                  {patientOpen && patientMatches.length > 0 && (
                    <div
                      className='absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-sm'
                      onMouseDown={event => event.preventDefault()}
                    >
                      {patientMatches.map(match => (
                        <button
                          key={match.id}
                          type='button'
                          className='block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground'
                          onClick={() => {
                            setPatientFilter(match);
                            setPatientQuery('');
                            setPatientMatches([]);
                            setPatientOpen(false);
                            setPage(0);
                          }}
                        >
                          {match.last_name}, {match.first_name}
                          {match.mrn ? ` (${match.mrn})` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <Select
              value={typeFilter}
              onValueChange={value => {
                setTypeFilter(value);
                setPage(0);
              }}
            >
              <SelectTrigger
                className='w-full sm:w-48'
                aria-label='Filter by type'
              >
                <SelectValue placeholder='Any type' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_TYPE}>Any type</SelectItem>
                {typeOptions.map(type => (
                  <SelectItem key={type.id} value={type.id}>
                    {/* Marked, so filtering by one is not mistaken for it
                        still being on offer when classifying. */}
                    {type.is_active ? type.name : `${type.name} (retired)`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className='flex items-center gap-2'>
              <Switch
                id='show-filed'
                checked={includeFiled}
                onCheckedChange={checked => {
                  setIncludeFiled(checked);
                  setPage(0);
                }}
              />
              <Label htmlFor='show-filed' className='whitespace-nowrap'>
                Show merged
              </Label>
            </div>
          </div>

          {loading ? (
            <div className='flex justify-center py-10'>
              <Loader2 className='h-6 w-6 animate-spin' />
            </div>
          ) : loadFailed ? (
            <div className='space-y-3 py-10 text-center'>
              <p className='text-destructive'>Could not load the fax inbox.</p>
              {/* A blip should not cost a page reload, and without this the
                  only way back is to change a filter. */}
              <Button variant='outline' onClick={() => void load()}>
                Try again
              </Button>
            </div>
          ) : items.length === 0 ? (
            <p className='py-10 text-center text-muted-foreground'>
              No faxes here yet.
            </p>
          ) : (
            <>
              <div className='overflow-x-auto'>
                <Table className='min-w-[720px]'>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-10'>
                        <Checkbox
                          aria-label='Select all faxes'
                          checked={allVisibleSelected}
                          onCheckedChange={toggleAllVisible}
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>Pages</TableHead>
                      <TableHead>Received</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Tags</TableHead>
                      <TableHead>Status</TableHead>
                      {canStartReferral && <TableHead>Referral</TableHead>}
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map(item => (
                      <TableRow
                        key={itemKey(item)}
                        className='cursor-pointer'
                        // Anywhere on the row, because the row is the fax and
                        // everything somebody opens it to do is in there. The
                        // checkbox and the buttons stop the event themselves,
                        // so they still do their own job.
                        onClick={() => setEditing(item)}
                      >
                        <TableCell onClick={event => event.stopPropagation()}>
                          <Checkbox
                            aria-label={`Select ${displayName(item)}`}
                            checked={selectedByKey.has(itemKey(item))}
                            onCheckedChange={() => toggleItem(item)}
                          />
                        </TableCell>
                        <TableCell className='font-medium'>
                          <span className='flex items-center gap-2'>
                            {displayName(item)}
                            {item.filed && (
                              <Badge variant='outline'>Merged</Badge>
                            )}
                          </span>
                        </TableCell>
                        <TableCell>
                          <SenderCell item={item} />
                        </TableCell>
                        <TableCell>{item.page_count ?? '—'}</TableCell>
                        <TableCell>
                          {formatDateTimeInPT(item.received_at)}
                        </TableCell>
                        <TableCell>
                          {item.fax_type_name ?? (
                            <span className='text-muted-foreground'>—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.tags && item.tags.length > 0 ? (
                            <span className='flex flex-wrap gap-1'>
                              {item.tags.map(tag => (
                                <Badge key={tag.id} variant='secondary'>
                                  {tag.name}
                                </Badge>
                              ))}
                            </span>
                          ) : (
                            <span className='text-muted-foreground'>—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={item.status} />
                        </TableCell>
                        {/* Shown rather than enforced. The same pages can
                            legitimately start a second referral, so this says
                            what already happened instead of blocking it.
                            Hidden along with the action, because a link to a
                            screen the reader cannot open is a dead end. */}
                        {canStartReferral && (
                          <TableCell>
                            {item.intake_id ? (
                              isExtracting(item) ? (
                                // Still being read, so this points at the
                                // Intake Manager, which is where that progress
                                // is shown. The referral itself would open as
                                // an empty form filling in underneath somebody.
                                <Link
                                  className='inline-flex items-center gap-1.5 text-muted-foreground'
                                  to='/intake-manager'
                                >
                                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                                  Processing
                                </Link>
                              ) : (
                                <Link
                                  className='text-primary underline'
                                  to={`/intake/${item.intake_id}`}
                                >
                                  Open referral
                                </Link>
                              )
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell
                          className='text-right'
                          onClick={event => event.stopPropagation()}
                        >
                          <div className='flex justify-end gap-1'>
                            <Button
                              variant='ghost'
                              size='sm'
                              aria-label={`Open ${displayName(item)}`}
                              onClick={() => void openItem(item)}
                            >
                              <Download className='h-4 w-4' />
                            </Button>
                            <Button
                              variant='ghost'
                              size='sm'
                              aria-label={`Remove ${displayName(item)}`}
                              onClick={() => setRemoving(item)}
                            >
                              <Trash2 className='h-4 w-4' />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className='flex items-center justify-between'>
                <p className='text-sm text-muted-foreground'>
                  {total} {total === 1 ? 'fax' : 'faxes'}
                </p>
                <div className='flex items-center gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    disabled={page === 0}
                    onClick={() => setPage(current => Math.max(0, current - 1))}
                  >
                    Previous
                  </Button>
                  <span className='text-sm text-muted-foreground'>
                    Page {page + 1} of {lastPage + 1}
                  </span>
                  <Button
                    variant='outline'
                    size='sm'
                    disabled={page >= lastPage}
                    onClick={() =>
                      setPage(current => Math.min(lastPage, current + 1))
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <SelectionActionBar
        items={selectedItems}
        onClear={() => setSelectedByKey(new Map())}
        onRemoveItem={item => deselectItem(item)}
        actions={selectionActions}
        getItemKey={item => itemKey(item)}
        getItemLabel={item =>
          `${displayName(item)} - ${item.page_count ?? '?'} pages`
        }
        getItemGroup={item => ({
          key: item.kind,
          label: item.kind === 'document' ? 'Documents' : 'Received',
        })}
      />

      <ConfirmationDialog
        open={removingMany !== null}
        title={
          removingMany && removingMany.length > 1
            ? `Remove ${removingMany.length} faxes?`
            : 'Remove this fax?'
        }
        message={
          removingMany
            ? `${removingMany.length === 1 ? 'It' : 'They'} will be taken out of the inbox. ${removingMany.length === 1 ? 'Its' : 'Their'} pages are kept and anything already built from ${removingMany.length === 1 ? 'it' : 'them'} is unaffected, so support can put ${removingMany.length === 1 ? 'it' : 'them'} back.`
            : ''
        }
        confirmLabel='Remove'
        onAccept={() => {
          if (removingMany) void removeSelection(removingMany);
        }}
        onCancel={() => setRemovingMany(null)}
      />

      <ConfirmationDialog
        open={removing !== null}
        title='Remove this fax?'
        message={
          removing
            ? `${displayName(removing)} will be taken out of the inbox. Its pages are kept and anything already built from it is unaffected, so support can put it back.`
            : ''
        }
        confirmLabel='Remove'
        onAccept={() => {
          if (removing) void remove(removing);
        }}
        onCancel={() => setRemoving(null)}
      />

      <PageEditorDialog
        item={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          showSuccess('Saved.');
          void load();
        }}
        onSplit={remaining => {
          showSuccess('Split into a new fax.');
          // Carry on with what is left rather than closing: taking a stapled
          // fax apart is usually more than one cut, and this makes the second
          // cut free. The item is replaced because the document's version
          // moved, or because an arrival has just become a document.
          setEditing(remaining);
          void load();
        }}
        onError={showError}
        onConflict={() => {
          // Somebody else re-baked it while this was open. Reloading is the
          // honest response: the pages on screen described a document that no
          // longer exists in that shape.
          showError(
            'Somebody else changed that document. It has been reloaded, so please try again.'
          );
          void load();
        }}
      />
    </div>
  );
}
